/*
 * CART OFFERS
 * ---------------------------------------------------------------------------------------------------------------------
 * Multi-stage cart progress bar (free shipping, free gift, % off...) with gift auto-add.
 * Replaces the Rebuy smart cart. Offers are configured in Theme settings > Cart; discounts themselves are managed in
 * Shopify admin — this file only visualises progress and keeps gift lines in sync with the thresholds.
 *
 * Gift lines are added with the hidden property `_cart_offer: <tier id>`. They never count towards progress, and are
 * removed automatically when the cart drops back below their threshold. If a customer removes an auto-added gift it
 * is never re-added automatically (they get an "Add" button instead), even if the cart drops below the threshold and
 * goes back over it. That choice is kept in localStorage, so it holds across tabs and visits, and is reset once the
 * cart has nothing but gifts in it (emptied, or an order placed).
 *
 * Each configured tier allows exactly one gift: a single line at quantity 1. Extra quantity or duplicate lines for the
 * same tier (from another tab, a re-applied discount, a direct /cart/add call...) are trimmed back on every evaluation.
 *
 * PUBLIC API (window.CartOffers)
 * ---------------------------------------------------------------------------------------------------------------------
 *   CartOffers.state                          -> { total, tiers: [{ id, threshold, unlocked, remaining, inCart, ... }] }
 *   CartOffers.on(name, fn)                   -> subscribe, returns an unsubscribe function. Names below.
 *   CartOffers.addProduct(variantId, opts)    -> add any variant; opts: { quantity, properties, tier }. Passing a tier
 *                                                tags it as an offer line (excluded from progress, shown as a gift).
 *                                                A configured tier id (e.g. 'cart_offer_2') is also auto-removed when
 *                                                that tier locks; any other value becomes 'custom:<value>' and is
 *                                                left for your code to manage.
 *   CartOffers.claim(tierId)                  -> add a tier's gift now (clears a previous customer removal)
 *   CartOffers.setTierProduct(tierId, variantId|null)
 *   CartOffers.registerTier(tier)             -> add/replace a tier at runtime (threshold in cents, shop currency)
 *   CartOffers.removeTier(tierId)
 *   CartOffers.refresh()                      -> refetch the cart and re-evaluate
 *
 * EVENTS (dispatched on document as `cart-offers:<name>`, detail shown)
 * ---------------------------------------------------------------------------------------------------------------------
 *   change      { state }                              every evaluation
 *   unlocked    { tier, state }                        a tier crossed its threshold (not fired on first page load)
 *   locked      { tier, state }                        a tier dropped below its threshold
 *   before-add  { tier, variantId, quantity, properties }  CANCELABLE. Mutate detail to swap the product, or call
 *                                                      event.preventDefault() to stop the auto-add.
 *   added       { tier, items }
 *   removed     { tier, keys }
 *   ready       { api }
 *
 * Example — swap the gift for members, and auto-add a sample when the gift tier unlocks:
 *   document.addEventListener('cart-offers:ready', () => {
 *     CartOffers.on('before-add', (e) => { if (window.isMember) e.detail.variantId = 123456789; });
 *     CartOffers.on('unlocked', (e) => { if (e.detail.tier.id === 'cart_offer_2') CartOffers.addProduct(987654321, { tier: 'sample' }); });
 *   });
 */
(() => {
  if (window.CartOffers) return;

  const config = window.CartOffersConfig || { enabled: false, tiers: [] };
  const OFFER_PROPERTY = '_cart_offer';
  const STORAGE_KEY = 'cart-offers';
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------------------------------------------- */

  const rate = () => parseFloat(window.Shopify?.currency?.rate || 1) || 1;

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const fillTemplate = (template, tier, remaining) => escapeHtml(template)
    .replace(/\[label\]/gi, escapeHtml(tier?.label || ''))
    .replace(/\[amount\]/gi, `<strong>${escapeHtml(formatMoney(Math.max(0, remaining || 0)))}</strong>`);

  const isOfferLine = (item) => Boolean(item.properties && item.properties[OFFER_PROPERTY]);

  // A line of the tier's gift product counts as "the gift is in the cart" even without the _cart_offer tag (added by
  // another app, from the product page, or a snapshot without the property), so the Add button is not offered for it.
  // These untagged lines are never removed or trimmed by this file.
  const isTierProductLine = (item, tier) => {
    if (!tier?.product) return false;
    if (tier.product.productId && item.product_id === tier.product.productId) return true;
    return Boolean(tier.product.variantId) && item.variant_id === tier.product.variantId;
  };

  const hasGiftFor = (items, tier) => (items || []).some((item) => item.properties?.[OFFER_PROPERTY] === tier.id || isTierProductLine(item, tier));

  // localStorage so a removed gift stays removed in other tabs and later visits; the in-memory copy covers browsers
  // where storage is blocked, so a removal is still respected for the life of the page
  const emptyStore = () => ({ added: {}, dismissed: {}, failed: {} });
  let memoryStore = null;

  const storage = {
    read() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (saved) return { ...emptyStore(), ...saved };
      } catch (e) {}
      return memoryStore ? JSON.parse(JSON.stringify(memoryStore)) : emptyStore();
    },
    write(value) {
      memoryStore = value;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (e) {}
    }
  };

  const emit = (name, detail, cancelable = false) => {
    const event = new CustomEvent(`cart-offers:${name}`, { bubbles: true, cancelable, detail });
    document.dispatchEvent(event);
    return event;
  };

  /* ---------------------------------------------------------------------------------------------------------------
   * Engine
   * ------------------------------------------------------------------------------------------------------------- */

  let tiers = (config.tiers || []).map((tier) => ({ ...tier }));
  let cart = config.cart || { items: [], cart_level_discount_applications: [] };
  let state = null;
  let previousUnlocked = null; // null until the first evaluation, so page loads don't fire "unlocked"
  let celebrateId = null;
  let newIds = new Set();
  let newAt = 0;
  let syncing = false;
  let pendingCart = null;
  let ignoreNextRefresh = false;
  let syncHistory = [];

  const sortedTiers = () => tiers.filter((tier) => tier.threshold > 0).sort((a, b) => a.threshold - b.threshold);

  const qualifyingTotal = (cart) => {
    const items = (cart.items || []).filter((item) => !isOfferLine(item));

    if (config.basis === 'before_discounts') {
      return items.reduce((sum, item) => sum + item.original_line_price, 0);
    }

    const lines = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
    return Math.max(0, lines - cartDiscounts);
  };

  const computeState = (cart) => {
    const total = qualifyingTotal(cart);
    const store = storage.read();
    const ordered = sortedTiers();

    const computed = ordered.map((tier, index) => {
      const threshold = Math.round(tier.threshold * rate());
      const giftLines = (cart.items || []).filter((item) => item.properties?.[OFFER_PROPERTY] === tier.id);
      const hasGift = hasGiftFor(cart.items, tier);
      const unlocked = total >= threshold;

      return {
        ...tier,
        index,
        threshold,
        unlocked,
        remaining: Math.max(0, threshold - total),
        inCart: giftLines.length > 0,
        hasGift,
        lineKeys: giftLines.map((item) => item.key),
        giftQuantity: giftLines.reduce((sum, item) => sum + (item.quantity || 0), 0),
        dismissed: Boolean(store.dismissed[tier.id]),
        claimable: unlocked && Boolean(tier.product?.variantId) && tier.product?.available !== false && !hasGift
      };
    });

    const next = computed.find((tier) => !tier.unlocked) || null;
    const max = computed.length ? computed[computed.length - 1].threshold : 0;

    return { total, tiers: computed, next, max, complete: computed.length > 0 && !next };
  };

  const progressFor = (current) => {
    const { tiers, total, max } = current;
    if (!tiers.length || !max) return 0;

    if (config.spacing === 'proportional') return Math.min(1, total / max);

    // Even spacing: each segment between two steps is the same width, whatever the amounts
    const count = tiers.length;
    let previous = 0;
    for (let index = 0; index < count; index++) {
      const threshold = tiers[index].threshold;
      if (total < threshold) return (index + (total - previous) / (threshold - previous)) / count;
      previous = threshold;
    }
    return 1;
  };

  const positionFor = (tier, current) => config.spacing === 'proportional'
    ? (tier.threshold / current.max) * 100
    : ((tier.index + 1) / current.tiers.length) * 100;

  const postJson = async (url, body) => {
    const response = await fetch(`${root()}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(json.description || json.message || 'Cart request failed'), { response: json });
    return json;
  };

  /**
   * Tell the rest of the theme that we changed the cart behind its back.
   *
   * This fires `cart:refresh`, not `cart:change`, on purpose. The cart drawer answers a `cart:change` by writing a
   * snapshot of the cart HTML — captured when the customer's own request was made — into the existing
   * `.cart-drawer__inner` 1250ms later. That snapshot still contains the gift we have since removed, so it would
   * resurrect removed lines. `cart:refresh` makes the drawer replace its children with freshly rendered markup, which
   * also detaches the node that pending write targets, so the stale snapshot lands harmlessly off-screen.
   */
  const broadcastCart = async () => {
    const freshCart = await fetch(`${root()}cart.js`).then((r) => r.json());
    cart = freshCart;

    // The cart page is server-rendered and the theme reloads it for its own changes, so do the same
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return freshCart;
    }

    ignoreNextRefresh = true;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return freshCart;
  };

  const addLines = async (lines) => {
    if (!lines.length) return [];
    const response = await postJson('cart/add.js', {
      items: lines.map(({ variantId, quantity, properties }) => ({ id: variantId, quantity, properties }))
    });
    return response.items || [response];
  };

  const removeLines = async (keys, trimKeys = []) => {
    if (!keys.length && !trimKeys.length) return;
    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    trimKeys.forEach((key) => (updates[key] = 1));
    await postJson('cart/update.js', { updates });
  };

  // A configured offer only ever gives one gift, whatever quantity is asked for
  const buildGiftLine = (tier, variantId = tier.product?.variantId, extra = {}) => ({
    tier,
    variantId,
    quantity: 1,
    properties: { ...(extra.properties || {}), [OFFER_PROPERTY]: tier.id }
  });

  const giftLinesFor = (tierId) => (cart.items || []).filter((item) => item.properties?.[OFFER_PROPERTY] === tierId);

  /**
   * Work out which gift lines need adding/removing for the current cart.
   */
  const planSync = (current) => {
    // Nothing but gifts (or nothing at all) in the cart: a new cart, so forget earlier removals
    if (!(cart.items || []).some((item) => !isOfferLine(item))) storage.write(emptyStore());

    const store = storage.read();
    const toAdd = [];
    const toRemove = [];
    const toTrim = [];

    current.tiers.forEach((tier) => {
      if (!tier.unlocked) {
        if (tier.inCart) toRemove.push({ tier, keys: tier.lineKeys });
        // A gift the customer removed stays dismissed: going back over the threshold shows the Add button instead
        delete store.added[tier.id];
        delete store.failed[tier.id];
        return;
      }

      if (tier.hasGift) {
        store.added[tier.id] = true;

        // Only one gift per offer: keep the first line at quantity 1 and drop any duplicates
        if (tier.inCart && tier.giftQuantity > 1) {
          const [keep, ...extra] = giftLinesFor(tier.id);
          if (keep.quantity > 1) toTrim.push(keep.key);
          if (extra.length) toRemove.push({ tier, keys: extra.map((item) => item.key) });
        }
        return;
      }

      // Unlocked, gift not in the cart: if we added it before, the customer removed it — respect that.
      if (store.added[tier.id]) {
        store.dismissed[tier.id] = true;
        delete store.added[tier.id];
      }

      if (tier.autoAdd && tier.claimable && !store.dismissed[tier.id] && !store.failed[tier.id]) {
        toAdd.push(buildGiftLine(tier));
      }
    });

    // Remove offer lines belonging to tiers that no longer exist (e.g. an offer switched off in the editor)
    const knownIds = new Set(current.tiers.map((tier) => tier.id));
    const orphanKeys = (cart.items || []).filter((item) => isOfferLine(item) && !knownIds.has(item.properties[OFFER_PROPERTY]) && !String(item.properties[OFFER_PROPERTY]).startsWith('custom:')).map((item) => item.key);
    if (orphanKeys.length) toRemove.push({ tier: null, keys: orphanKeys });

    storage.write(store);
    return { toAdd, toRemove, toTrim };
  };

  const runSync = async (plan) => {
    // Circuit breaker: never loop more than 4 syncs in 10 seconds
    const now = Date.now();
    syncHistory = syncHistory.filter((time) => now - time < 10000);
    if (syncHistory.length >= 4) return;
    syncHistory.push(now);

    const approved = plan.toAdd.filter((line) => {
      const event = emit('before-add', line, true);
      line.quantity = 1; // listeners may swap the product, not the quantity
      return !event.defaultPrevented && line.variantId;
    });

    if (!approved.length && !plan.toRemove.length && !plan.toTrim.length) return;

    syncing = true;
    try {
      await removeLines(plan.toRemove.flatMap((entry) => entry.keys), plan.toTrim);
      plan.toRemove.forEach((entry) => emit('removed', { tier: entry.tier, keys: entry.keys }));

      if (approved.length) {
        try {
          const items = await addLines(approved);
          const store = storage.read();
          approved.forEach((line) => (store.added[line.tier.id] = true));
          storage.write(store);
          approved.forEach((line) => emit('added', { tier: line.tier, items: items.filter((item) => item.properties?.[OFFER_PROPERTY] === line.tier.id) }));
        } catch (error) {
          // Most likely out of stock — don't retry this session
          const store = storage.read();
          approved.forEach((line) => (store.failed[line.tier.id] = true));
          storage.write(store);
          console.warn('[CartOffers] Could not add gift', error);
        }
      }

      syncing = false;
      await broadcastCart();
    } catch (error) {
      console.warn('[CartOffers] Sync failed', error);
    } finally {
      syncing = false;
      const next = pendingCart || cart;
      pendingCart = null;
      evaluate(next);
    }
  };

  const evaluate = (newCart = cart) => {
    if (!config.enabled) return;
    cart = newCart;

    if (syncing) {
      pendingCart = newCart;
      return;
    }

    const previousState = state;
    state = computeState(cart);

    const unlockedIds = new Set(state.tiers.filter((tier) => tier.unlocked).map((tier) => tier.id));
    const unlockedNow = [];
    if (previousUnlocked) {
      state.tiers.forEach((tier) => {
        if (tier.unlocked && !previousUnlocked.has(tier.id)) unlockedNow.push(tier);
        if (!tier.unlocked && previousUnlocked.has(tier.id)) {
          celebrateId = null;
          emit('locked', { tier, state });
        }
      });
      unlockedNow.forEach((tier) => emit('unlocked', { tier, state }));
    }
    previousUnlocked = unlockedIds;

    if (unlockedNow.length) {
      celebrateId = unlockedNow[unlockedNow.length - 1].id;
      newIds = new Set(unlockedNow.map((tier) => tier.id));
      newAt = Date.now();
    }

    const plan = planSync(state);
    state = computeState(cart); // dismissed flags may have changed during planning

    // Animation bookkeeping, kept here (not on the element) because the drawer swaps in fresh HTML after each change
    state.progress = progressFor(state);
    state.fromProgress = previousState ? previousState.progress : 0;
    state.changedAt = !previousState || previousState.progress !== state.progress ? Date.now() : previousState.changedAt;
    state.newIds = newIds;
    state.newAt = newAt;
    state.celebrate = state.next ? state.tiers.find((tier) => tier.id === celebrateId && tier.unlocked) || null : null;

    emit('change', { state });

    if (plan.toAdd.length || plan.toRemove.length || plan.toTrim.length) runSync(plan);
  };

  const refresh = async () => {
    const freshCart = await fetch(`${root()}cart.js`).then((r) => r.json());
    evaluate(freshCart);
    return state;
  };

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.cart?.items) evaluate(event.detail.cart);
  });
  document.addEventListener('cart:refresh', () => {
    // Our own broadcast: the cart is already applied, no need to fetch it again
    if (ignoreNextRefresh) {
      ignoreNextRefresh = false;
      return;
    }
    refresh();
  });
  window.addEventListener('pageshow', (event) => event.persisted && refresh());

  /* ---------------------------------------------------------------------------------------------------------------
   * Public API
   * ------------------------------------------------------------------------------------------------------------- */

  const findTier = (tierId) => tiers.find((tier) => tier.id === tierId);

  const api = {
    get config() {
      return config;
    },
    get state() {
      return state;
    },
    get tiers() {
      return sortedTiers();
    },
    formatMoney,
    fillTemplate,

    on(name, handler) {
      const listener = (event) => handler(event);
      document.addEventListener(`cart-offers:${name}`, listener);
      return () => document.removeEventListener(`cart-offers:${name}`, listener);
    },

    async addProduct(variantId, { quantity = 1, properties = {}, tier = null } = {}) {
      const tierObject = tier ? findTier(tier) || { id: String(tier).startsWith('custom:') ? tier : `custom:${tier}` } : null;
      const lineProperties = tierObject ? { ...properties, [OFFER_PROPERTY]: tierObject.id } : properties;
      const isConfiguredTier = Boolean(tierObject && findTier(tierObject.id));

      // A configured offer's gift can only be in the cart once, at quantity 1
      if (isConfiguredTier) {
        const freshCart = await fetch(`${root()}cart.js`).then((r) => r.json());
        if (hasGiftFor(freshCart.items, findTier(tierObject.id))) {
          evaluate(freshCart);
          return [];
        }
        quantity = 1;
      }

      const items = await addLines([{ variantId, quantity, properties: lineProperties }]);
      if (isConfiguredTier) {
        const store = storage.read();
        store.added[tierObject.id] = true;
        storage.write(store);
      }
      const freshCart = await broadcastCart();
      evaluate(freshCart);
      return items;
    },

    async claim(tierId) {
      const tier = (state?.tiers || []).find((entry) => entry.id === tierId);
      if (!tier || !tier.claimable) return null;

      // The state may be stale (another tab, a double click): never add a second gift for the same offer
      const freshCart = await fetch(`${root()}cart.js`).then((r) => r.json());
      if (hasGiftFor(freshCart.items, tier)) {
        evaluate(freshCart);
        return null;
      }

      const store = storage.read();
      delete store.dismissed[tierId];
      delete store.failed[tierId];
      storage.write(store);

      const line = buildGiftLine(tier);
      const event = emit('before-add', line, true);
      line.quantity = 1;
      if (event.defaultPrevented || !line.variantId) return null;

      const items = await addLines([line]);
      store.added[tierId] = true;
      storage.write(store);
      emit('added', { tier, items });
      evaluate(await broadcastCart());
      return items;
    },

    setTierProduct(tierId, variantId) {
      const tier = findTier(tierId);
      if (!tier) return;
      tier.product = variantId ? { ...(tier.product || {}), productId: null, variantId, available: true } : null;
      evaluate();
    },

    registerTier(tier) {
      if (!tier?.id) throw new Error('CartOffers.registerTier: tier.id is required');
      const defaults = { type: 'reward', label: '', lockedText: 'Spend [amount] more for [label]', unlockedText: "You've unlocked [label]", autoAdd: true, product: null };
      tiers = tiers.filter((existing) => existing.id !== tier.id).concat({ ...defaults, ...tier });
      evaluate();
    },

    removeTier(tierId) {
      tiers = tiers.filter((tier) => tier.id !== tierId);
      evaluate();
    },

    refresh
  };

  window.CartOffers = api;

  /* ---------------------------------------------------------------------------------------------------------------
   * <cart-offers> progress bar
   * ------------------------------------------------------------------------------------------------------------- */

  class CartOffersElement extends HTMLElement {
    connectedCallback() {
      this._unsubscribe = api.on('change', () => this.render());
      // Labels can wrap to two lines; reserve their height (also re-measures when a closed drawer becomes visible)
      this._resizeObserver = new ResizeObserver(() => this._measureLabels());
      this._resizeObserver.observe(this);
      this.render();
    }

    disconnectedCallback() {
      this._unsubscribe?.();
      this._resizeObserver?.disconnect();
    }

    _measureLabels() {
      const heights = [...this.querySelectorAll('.cart-offers__label')].map((label) => label.getBoundingClientRect().height);
      const tallest = Math.max(0, ...heights);
      if (tallest > 0) this.style.setProperty('--cart-offers-label-height', `${Math.ceil(tallest)}px`);
    }

    render() {
      const current = state;
      if (!current || !current.tiers.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const ANIMATION_WINDOW = 800;
      const progress = current.progress;
      const newlyUnlocked = Date.now() - current.newAt < ANIMATION_WINDOW ? current.newIds : new Set();

      // Message: next locked tier, or the "complete" message
      let message;
      if (current.next) {
        message = fillTemplate(current.next.lockedText || 'Spend [amount] more for [label]', current.next, current.next.remaining);
      } else {
        const last = current.tiers[current.tiers.length - 1];
        message = fillTemplate(config.completeText || last.unlockedText, last, 0);
      }

      // The most recently unlocked tier gets its own line above the next goal
      const celebrate = current.celebrate;
      const celebration = celebrate?.unlockedText ? `<span class="cart-offers__celebrate">${fillTemplate(celebrate.unlockedText, celebrate, 0)}</span>` : '';

      const steps = current.tiers.map((tier) => {
        const classes = ['cart-offers__step', tier.unlocked ? 'is-unlocked' : 'is-locked', newlyUnlocked.has(tier.id) ? 'is-new' : '', tier === current.next ? 'is-next' : ''].filter(Boolean).join(' ');
        const status = tier.unlocked ? 'unlocked' : `${formatMoney(tier.remaining)} to go`;
        return `
          <li class="${classes}" style="--position: ${positionFor(tier, current)}%">
            <span class="cart-offers__marker" aria-hidden="true">
              <span class="cart-offers__icon">${config.icons?.[tier.type] || config.icons?.reward || ''}</span>
              <svg class="cart-offers__check" viewBox="0 0 12 12" width="10" height="10"><path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
            <span class="cart-offers__label">${escapeHtml(tier.label)}</span>
            <span class="sr-only">${escapeHtml(tier.label)} (${formatMoney(tier.threshold)}): ${escapeHtml(status)}</span>
          </li>`;
      }).join('');

      const claims = current.tiers.filter((tier) => tier.claimable && (!tier.autoAdd || tier.dismissed)).map((tier) => `
        <button type="button" class="cart-offers__claim" data-claim="${escapeHtml(tier.id)}">
          <span class="cart-offers__claim-icon" aria-hidden="true">${config.icons?.gift || ''}</span>
          <span>${fillTemplate(config.claimLabel || 'Add [label]', tier, 0)}</span>
        </button>`).join('');

      const startProgress = Date.now() - current.changedAt < ANIMATION_WINDOW ? current.fromProgress : progress;

      const markup = `
        <p class="cart-offers__message" aria-live="polite">${celebration}<span>${message}</span></p>
        <div class="cart-offers__bar" style="--step-count: ${current.tiers.length}">
          <div class="cart-offers__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress * 100)}">
            <span class="cart-offers__fill" style="--progress: ${progress}"></span>
          </div>
          <ol class="cart-offers__steps" role="list">${steps}</ol>
        </div>
        ${claims ? `<div class="cart-offers__claims">${claims}</div>` : ''}`;

      // Nothing visible changed (e.g. the gift line just landed) — leave the DOM alone so animations don't restart
      if (markup === this._markup) return;
      this._markup = markup;
      this.innerHTML = markup;
      this._measureLabels();

      this.classList.toggle('is-complete', current.complete);
      this.classList.toggle('is-animating', Date.now() - Math.max(current.changedAt, current.newAt) < ANIMATION_WINDOW);

      const fill = this.querySelector('.cart-offers__fill');
      if (startProgress !== progress) {
        fill.style.setProperty('--progress', startProgress);
        getComputedStyle(fill).transform; // commit the start value so the change below transitions
        fill.style.setProperty('--progress', progress);
      }

      this.querySelectorAll('[data-claim]').forEach((button) => button.addEventListener('click', async () => {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        try {
          await api.claim(button.dataset.claim);
        } catch (error) {
          console.warn('[CartOffers] Claim failed', error);
          button.disabled = false;
        }
        button.removeAttribute('aria-busy');
      }));

    }
  }

  // Initial evaluation from the server-rendered cart snapshot (no extra request on page load)
  evaluate(cart);

  if (!window.customElements.get('cart-offers')) {
    window.customElements.define('cart-offers', CartOffersElement);
  }

  emit('ready', { api });
})();
